import { useEffect, useState } from "react";
import { sanitizeArticleData } from "@routes/Article/utils/sanitizeArticleData";
import { ArticlePageData, FeedArticleItems } from "@routes/Article/utils/types";
import { tryCatch } from "@typings/TryCatch";
import axios from "axios";

type UseNextPodcastArticles = {
  /** Urlpath of the article to fetch the next articles */
  urlPath?: string;
  /** Publish time of the article to fetch the next articles */
  afterPublishTime?: string;
  /** Flag to indicate if the article is a podcast audio article */
  isPodcastAudioArticle?: boolean;
};

/**
 * Hook to fetch podcast coming up next articles data based on the provided URL path and published time.
 */
export const useNextPodcastArticles = ({
  urlPath,
  afterPublishTime,
  isPodcastAudioArticle,
}: UseNextPodcastArticles) => {
  const [articles, setArticles] = useState<FeedArticleItems[]>([]);

  useEffect(() => {
    if (!isPodcastAudioArticle) return;

    const fetchData = async () => {
      const endpoint = "/_plat/api/v1/articles/next-from-podcasts";
      const payload = { tagUrlPath: urlPath, afterPublishTime, version: "6.0" };
      const request = axios.get<ArticlePageData[]>(endpoint, {
        params: payload,
      });

      const { data: response, error } = await tryCatch(request);

      if (error) {
        // eslint-disable-next-line no-console
        console.error(
          "Error fetching next podcast articles:",
          JSON.stringify({ endpoint, payload, error })
        );

        return;
      }

      const normalizedData =
        response?.data?.map((item) => sanitizeArticleData(item.articleData)) ||
        [];
      setArticles(normalizedData.slice(0, 8));
    };

    fetchData();
  }, [urlPath, afterPublishTime, isPodcastAudioArticle]);

  return { articles };
};
